import axios from 'axios';
import { CLIENT_ID, CLIENT_SECRET, API_BASE_URL, AUTH_BASE_URL } from '../config/api';

interface ClientTokenResponse {
    access_token: string;
    refresh_token: string;
    access_token_expiry: string;
    refresh_token_expiry: string;
}

interface LoginResponse {
    access_token: string;
    refresh_token: string;
    access_token_expiry: string;
    refresh_token_expiry: string;
    user: {
        id: number;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
    };
}

interface RegistrationData {
    full_name: string;
    username: string;
    password: string;
    email: string;
    university?: string;
    endowment_name?: string;
    activism_organization?: string;
    signal_id?: string;
}

interface RegistrationResponse {
    message: string;
    user_id: number;
}

interface ForgotPasswordResponse {
    message: string;
}

const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

// Create a separate authenticated client for all API calls
export const authenticatedApiClient = axios.create({
    baseURL: API_BASE_URL,
});

const getClientToken = async (): Promise<string> => {
    try {
        const response = await apiClient.post<ClientTokenResponse>(`${AUTH_BASE_URL}/token/`, {
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
        });

        return response.data.access_token;
    } catch (error: any) {
        throw new Error('Failed to get client authentication token');
    }
};

export const login = async (username: string, password: string): Promise<LoginResponse> => {
    try {
        // First, get client token
        const clientToken = await getClientToken();
        
        // Then, use client token to authenticate user
        const response = await apiClient.post<LoginResponse>(`${AUTH_BASE_URL}/login/`, {
            username,
            password,
        }, {
            headers: {
                'Authorization': `Bearer ${clientToken}`
            }
        });

        const { access_token, refresh_token, access_token_expiry, refresh_token_expiry, user } = response.data;
        
        // Store tokens, expiry timestamps, and user info in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('access_token_expiry', access_token_expiry);
        localStorage.setItem('refresh_token_expiry', refresh_token_expiry);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Set default authorization header for future requests
        setAuthToken(access_token);
        
        // Dispatch custom event to notify components about login
        window.dispatchEvent(new CustomEvent('auth:login'));
        
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Login failed');
    }
};

export const register = async (registrationData: RegistrationData): Promise<RegistrationResponse> => {
    try {
        // First, get client token
        const clientToken = await getClientToken();
        
        // Then, use client token to register user
        const response = await apiClient.post<RegistrationResponse>(`${AUTH_BASE_URL}/register/`, registrationData, {
            headers: {
                'Authorization': `Bearer ${clientToken}`
            }
        });

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Registration failed');
    }
};

export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
    try {
        // First, get client token
        const clientToken = await getClientToken();
        
        // Then, use client token to request password reset
        const response = await apiClient.post<ForgotPasswordResponse>(`${AUTH_BASE_URL}/forgot-password/`, { email }, {
            headers: {
                'Authorization': `Bearer ${clientToken}`
            }
        });

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Password reset failed');
    }
};

export const logout = (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('access_token_expiry');
    localStorage.removeItem('refresh_token_expiry');
    localStorage.removeItem('user');
    delete apiClient.defaults.headers.common['Authorization'];
    // Dispatch custom event to notify components about logout
    window.dispatchEvent(new CustomEvent('auth:logout'));    
};


export const setAuthToken = (token: string): void => {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const getStoredToken = (): string | null => {
    return localStorage.getItem('access_token');
};

export const isAuthenticated = (): boolean => {
    const token = getStoredToken();
    const expiry = localStorage.getItem('access_token_expiry');
    
    if (!token || !expiry) return false;
    
    try {
        // Use server-provided expiry timestamp
        const expiryDate = new Date(expiry);
        const currentDate = new Date();
        return expiryDate > currentDate;
    } catch {
        return false;
    }
};

export const isTokenExpired = (): boolean => {
    const token = getStoredToken();
    const expiry = localStorage.getItem('access_token_expiry');
    
    if (!token || !expiry) return true;
    
    try {
        // Use server-provided expiry timestamp
        const expiryDate = new Date(expiry);
        const currentDate = new Date();
        return expiryDate <= currentDate;
    } catch {
        return true;
    }
};

export const isRefreshTokenExpired = (): boolean => {
    const refreshToken = localStorage.getItem('refresh_token');
    const expiry = localStorage.getItem('refresh_token_expiry');
    
    if (!refreshToken || !expiry) return true;
    
    try {
        // Use server-provided expiry timestamp
        const expiryDate = new Date(expiry);
        const currentDate = new Date();
        return expiryDate <= currentDate;
    } catch {
        return true;
    }
};

export const refreshToken = async (): Promise<string | null> => {
    const refresh = localStorage.getItem('refresh_token');
    
    if (!refresh) {
        logout();
        return null;
    }

    // Check if refresh token is expired
    if (isRefreshTokenExpired()) {
        logout();
        return null;
    }

    try {
        const response = await apiClient.post<{
            access_token: string;
            refresh_token: string;
            access_token_expiry: string;
            refresh_token_expiry: string;
        }>(`${AUTH_BASE_URL}/refresh/`, {
            refresh
        });

        const { access_token, refresh_token, access_token_expiry, refresh_token_expiry } = response.data;
        
        // Store all updated tokens and expiry timestamps
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('access_token_expiry', access_token_expiry);
        localStorage.setItem('refresh_token_expiry', refresh_token_expiry);
        
        setAuthToken(access_token);
        
        // Dispatch event to notify components about successful token refresh
        window.dispatchEvent(new CustomEvent('auth:login'));
        
        return access_token;
    } catch (error: any) {
        if (error.response?.status === 401) {
            logout();
        }
        return null;
    }
};


// Initialize auth token on app start if available
const initializeAuth = async (): Promise<boolean> => {
    const token = getStoredToken();
    if (!token) {
        return false;
    }

    // If token is still valid, set it and return true
    if (isAuthenticated()) {
        setAuthToken(token);
        return true;
    }

    // If token is expired, try to refresh it
    try {
        const newToken = await refreshToken();
        if (newToken) {
            return true;
        }
    } catch (error) {
        // Token refresh failed, user needs to login
    }

    return false;
};

export const getCurrentUser = (): any | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
};

// Function to ensure valid token before API calls
export const ensureValidToken = async (): Promise<string | null> => {
    // Check if we have a token
    const currentToken = getStoredToken();
    if (!currentToken) {
        logout();
        return null;
    }
    // If token is not expired, return it
    if (!isTokenExpired()) {
        return currentToken;
    }
    // Token is expired, try to refresh
    const newToken = await refreshToken();
    
    if (!newToken) {
        return null;
    }
    
    return newToken;
};


export { initializeAuth };

// Set up interceptors after all functions are defined
// Request interceptor to add auth token
authenticatedApiClient.interceptors.request.use(
    (config) => {
        // Add current token if available
        const token = getStoredToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh on 401
authenticatedApiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Check if we should try to refresh
            if (isTokenExpired() && isRefreshTokenExpired()) {
                logout();
                return Promise.reject(error);
            }

            try {
                const newToken = await refreshToken();
                if (newToken) {
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return authenticatedApiClient(originalRequest);
                }
            } catch (refreshError) {
                logout();
                return Promise.reject(refreshError);
            }
        }

        if (error.response?.status === 401) {
            logout();
        }

        return Promise.reject(error);
    }
);
